//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import {
  CONTACT_SUPPORT_MESSAGE,
  MAXIMUM_PINNED_ITEMS_COUNT,
} from '@like2buy-v2/Like2BuyV2UIConstants';
import { SocialNetwork } from '@models/SocialNetwork';
import { pluralize } from '@utils/stringUtils';

const baseToast = {
  options: {
    toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
  },
};

export const addLike2BuyV2ItemsSucceededToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Media Added to Like2Buy',
    message: `Successfully added ${pluralize(
      itemCount,
      'item',
    )} to your ${network} Like2Buy store.`,
  };
};

export const addLike2BuyV2ItemsFailedToast = (
  network: SocialNetwork,
): IToastNotification => ({
  ...baseToast,
  title: 'Failed to Add Media to Like2Buy',
  message: `We were unable to add media to your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
  type: ToastType.ERROR,
});

export const archiveLike2BuyV2ItemsFailedToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Failed to Archive Media from Like2Buy',
    message: `We were unable to archive ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
    type: ToastType.ERROR,
  };
};

export const archiveLike2BuyV2ItemsSucceededToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Media Archived from Like2Buy',
    message: `Successfully archived ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store.`,
    type: ToastType.SUCCESS,
  };
};

export const unarchiveLike2BuyV2ItemsFailedToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Failed to Unarchive Media to Like2Buy',
    message: `We were unable to unarchive ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
    type: ToastType.ERROR,
  };
};

export const unarchiveLike2BuyV2ItemsSucceededToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Media Unarchived from Like2Buy',
    message: `Successfully unarchived ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store.`,
    type: ToastType.SUCCESS,
  };
};

export const getLike2BuyV2StoreFailedToast = (
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Failed to Find Like2Buy Store',
    message: `We were unable to find your ${network} Like2Buy store. Please verify that your ${network} account is properly connected or contact Bazaarvoice Support.`,
    type: ToastType.ERROR,
  };
};

export const getLike2BuyV2PreviewFailedToast = (
  network: SocialNetwork,
): IToastNotification => ({
  ...baseToast,
  title: 'Failed to Open Like2Buy Preview',
  message: `We were unable to generate a preview for your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
  type: ToastType.ERROR,
});

export const pinLike2BuyV2ItemsSucceededToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Media Pinned in Like2Buy',
    message: `Successfully pinned ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store.`,
    type: ToastType.SUCCESS,
  };
};

export const unpinLike2BuyV2ItemsSucceededToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Media Unpinned in Like2Buy',
    message: `Successfully unpinned ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store.`,
    type: ToastType.SUCCESS,
  };
};

export const unpinLike2BuyV2ItemsFailedToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: `Failed to Unpin Items from Like2Buy`,
    message: `We were unable to unpin ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
    type: ToastType.ERROR,
  };
};

export const pinLike2BuyV2ItemsFailedToast = (
  itemCount: number,
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Failed to Pin Items from Like2Buy',
    message: `We were unable to pin ${pluralize(
      itemCount,
      'item',
    )} from your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
    type: ToastType.ERROR,
  };
};

export const reorderLike2BuyV2PinnedItemsFailedToast = (
  network: SocialNetwork,
) => ({
  ...baseToast,
  title: 'Failed to Reorder Pinned Items from Like2Buy',
  message: `We were unable to reorder pinned items from your ${network} Like2Buy store. ${CONTACT_SUPPORT_MESSAGE}`,
  type: ToastType.ERROR,
});

export const like2BuyV2MaxPinnedItemsWarningToast = (
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Maximum Pinned Items in Like2Buy',
    message: `You've reached the maximum number of pinned items in your ${network} Like2Buy store. Unpin items to pin more.`,
    type: ToastType.WARNING,
  };
};

export const maxPinsLike2BuyV2ItemsFailedToast = (
  network: SocialNetwork,
): IToastNotification => {
  return {
    ...baseToast,
    title: 'Failed to Pin Items in Like2Buy',
    message: `Your ${network} Like2Buy store can not have more than ${MAXIMUM_PINNED_ITEMS_COUNT} pinned items. Please select a number in this range and try again.`,
    type: ToastType.ERROR,
  };
};
